/**
 * Wave 3.59 — Cutter master form dialog. Basic create/edit for the new
 * Cutter party type (used exclusively for issuing broken packets for
 * recutting). Mirrors the Vendor form dialog but with far fewer fields
 * because a cutter is a light-touch craftsmanship party.
 */
import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { extractError } from '@/lib/errors';
import { Scissors } from 'lucide-react';

export function CutterFormDialog({ open, onOpenChange, cutter, companyId, onSaved }) {
  const editing = Boolean(cutter?.id);
  const [form, setForm] = useState({
    name: '', contact_person: '', phone: '', email: '',
    address: '', specialization: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      name: cutter?.name || '',
      contact_person: cutter?.contact_person || '',
      phone: cutter?.phone || '',
      email: cutter?.email || '',
      address: cutter?.address || '',
      specialization: cutter?.specialization || '',
      notes: cutter?.notes || '',
    });
  }, [open, cutter?.id]);

  const submit = async () => {
    if (!form.name?.trim()) {
      toast.error('Cutter name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, company_id: companyId };
      if (editing) {
        await api.put(`/masters/cutters/${cutter.id}`, payload);
        toast.success('Cutter updated');
      } else {
        await api.post('/masters/cutters', payload);
        toast.success('Cutter created');
      }
      onOpenChange(false);
      if (typeof onSaved === 'function') onSaved();
    } catch (e) {
      toast.error(extractError(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="cutter-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scissors size={18} className="text-[var(--ink-navy)]" />
            {editing ? `Edit Cutter · ${cutter?.name}` : 'New Cutter'}
          </DialogTitle>
          <DialogDescription>
            Cutters receive broken raw-material packets for recutting. They are separate from Goldsmiths and Vendors.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name <span className="text-rose-600">*</span></Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="cutter-form-name"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Phone</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                data-testid="cutter-form-phone"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="cutter-form-email"
              />
            </div>
          </div>
          <div>
            <Label>Contact Person</Label>
            <Input
              value={form.contact_person}
              onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
            />
          </div>
          <div>
            <Label>Specialization</Label>
            <Input
              value={form.specialization}
              onChange={(e) => setForm({ ...form, specialization: e.target.value })}
              placeholder="e.g. Diamond cutter / Emerald cutter"
            />
          </div>
          <div>
            <Label>Address</Label>
            <Textarea
              rows={2}
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
            />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="cutter-form-save">
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Cutter'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
